import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import { Search, Close, VideoLibrary } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { videoService } from '../../services/api';

// Picks an existing video to attach to a "video share" story.
// Reuses videoService.getAll, which already supports search + pagination.
const VideoPickerModal = ({ open, onClose, onSelect }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ videos: [], total: 0 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchVideos = useCallback(async () => {
    if (!open) return;
    try {
      setLoading(true);
      setError('');
      const response = await videoService.getAll({
        search,
        page: page + 1,
        limit: rowsPerPage
      });
      setData({
        videos: response.docs || [],
        total: response.totalDocs || 0
      });
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err.response?.data?.error || t('stories.videoFetchError'));
    } finally {
      setLoading(false);
    }
  }, [open, search, page, rowsPerPage, t]);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setSearch('');
      setPage(0);
      setRowsPerPage(10);
    }
  }, [open]);

  // Debounce typing, fetch immediately on page/size change
  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(fetchVideos, search ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchVideos, open, search]);

  const handleSelect = (video) => {
    onSelect(video);
    onClose();
  };

  const titleOf = (video) => video.titleGr || video.titleEn || video.title || '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t('stories.pickVideoTitle')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          size="small"
          fullWidth
          placeholder={t('stories.searchVideos')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('stories.thumbnail')}</TableCell>
                <TableCell>{t('stories.titleField')}</TableCell>
                <TableCell>{t('stories.category')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : data.videos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t('stories.noVideosFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.videos.map((video) => (
                  <TableRow key={video._id} hover>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={video.thumbnailUrl || undefined}
                        sx={{ width: 64, height: 40, bgcolor: 'grey.200' }}
                      >
                        <VideoLibrary fontSize="small" />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{titleOf(video)}</Typography>
                    </TableCell>
                    <TableCell>
                      {video.categories?.length ? (
                        <Chip
                          label={
                            video.categories[0].nameGr ||
                            video.categories[0].nameEn ||
                            video.categories[0].name
                          }
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => handleSelect(video)}>
                        {t('stories.selectVideo')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoPickerModal;
